import { useEffect, useState } from 'react';
import {
  fetchLanes,
  fetchState,
  fetchSymbols,
  fetchTrades,
} from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { LaneCard } from '../components/LaneCard';
import { EquityCurve } from '../components/EquityCurve';
import { PositionsTable } from '../components/PositionsTable';
import { RecentTrades } from '../components/RecentTrades';

const REFRESH_INTERVAL = 300_000; // 5 min

function todayStartUtcMs(): number {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

export default function DashboardPage() {
  const [refreshTick, setRefreshTick] = useState(0);

  // Auto-refresh
  useEffect(() => {
    const timer = window.setInterval(() => setRefreshTick((v) => v + 1), REFRESH_INTERVAL);
    return () => window.clearInterval(timer);
  }, []);

  const { data: lanesData, loading: lanesLoading, error: lanesError } = useAsync(
    () => fetchLanes(),
    [refreshTick]
  );

  const { data: tradesData, loading: tradesLoading, error: tradesError } = useAsync(
    () => fetchTrades(200),
    [refreshTick]
  );

  const { data: symbolsData } = useAsync(
    () => fetchSymbols(),
    [refreshTick]
  );

  // Fetch latest prices for configured symbols
  const [prices, setPrices] = useState<Record<string, number>>({});
  useEffect(() => {
    const syms = symbolsData?.symbols ?? [];
    if (syms.length === 0) return;

    let cancelled = false;
    Promise.all(
      syms.map(async (sym) => {
        try {
          const s = await fetchState(sym);
          if (typeof s.latestPrice === 'number') return [sym, s.latestPrice] as const;
        } catch {
          // omit symbol on failure
        }
        return null;
      })
    ).then((results) => {
      if (cancelled) return;
      const map: Record<string, number> = {};
      for (const entry of results) {
        if (entry) map[entry[0]] = entry[1];
      }
      setPrices(map);
    });

    return () => { cancelled = true; };
  }, [symbolsData, refreshTick]);

  // Compute today's realized PnL (fills with time >= start of today UTC)
  const todayStart = todayStartUtcMs();
  const todayRealizedUsd = (tradesData?.fills ?? [])
    .filter((f) => f.time >= todayStart)
    .reduce((acc, f) => acc + (Number(f.netPnl) || 0), 0);

  const equityPoints = tradesData?.equityCurve?.map((p) => ({ time: p.time, equity: p.equity })) ?? [];

  const lanes = lanesData?.lanes ?? [];

  return (
    <div className="dashboard-page">
      {/* Header */}
      <div className="dashboard-toolbar">
        <div>
          <h2>实时监控</h2>
        </div>
        <div className="toolbar">
          <button className="refresh" onClick={() => setRefreshTick((v) => v + 1)}>
            刷新
          </button>
        </div>
      </div>

      {/* Lane Cards */}
      {lanesLoading && <p className="muted">Lane 加载中...</p>}
      {lanesError && <p className="error">Lane 加载失败: {lanesError}</p>}
      {!lanesLoading && !lanesError && lanes.length === 0 && (
        <p className="muted">暂无 Lane 数据</p>
      )}
      {lanes.map((lane) => (
        <LaneCard
          key={lane.id}
          lane={lane}
          prices={prices}
          todayRealizedUsd={todayRealizedUsd}
        />
      ))}

      {/* Equity Curve */}
      {tradesLoading && <p className="muted">权益曲线加载中...</p>}
      {tradesError && <p className="error">成交数据加载失败: {tradesError}</p>}
      {!tradesLoading && <EquityCurve points={equityPoints} />}

      {/* Two-column: Positions | Recent Trades */}
      {!tradesLoading && lanes.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
          <PositionsTable lane={lanes[0]} prices={prices} />
          <RecentTrades fills={tradesData?.fills ?? []} />
        </div>
      )}
      {!tradesLoading && lanes.length === 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
          <div className="card"><p className="muted" style={{ margin: 0 }}>无持仓</p></div>
          <RecentTrades fills={tradesData?.fills ?? []} />
        </div>
      )}
    </div>
  );
}
